import React, { useCallback, useMemo, useState } from "react";

import Alert from "veneer/Alert";
import Drawer from "veneer/Drawer";
import { showError, showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import { CheckFilledIcon, ErrorStopSignOutlinedIcon } from "veneer/SvgIcon";
import { Colors } from "veneer/Theme";
import Tooltip from "veneer/Tooltip";
import Typography from "veneer/Typography";

import { Device } from "cortex/ui/src/fleet/view/device/model";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { UPGRADE_DEVICES_FIRMWARE } from "cortex/ui/src/redux-store/devices/actions";

import BaseDrawer, {
  ErrorListItem,
  ListItem,
  ListWrapper,
  UpgradeDrawerProps,
  UpgradeEligibility,
  UpgradeRow,
} from "./BaseDrawer";

function getUpgradeEligibility(device: Device): UpgradeEligibility {
  if (device.device_update_disabled) {
    return {
      isValid: false,
      reason: "Remote updates are disabled for this device",
    };
  }

  const isOnLatestVersion = device.current_firmware === device.latest_firmware;
  if (isOnLatestVersion) {
    return {
      isValid: false,
      reason: "Device is not eligible for an upgrade",
    };
  }
  return { isValid: true };
}

export default function UpgradeFirmwareDrawer({
  onCancel,
  open,
  selectedItems,
  setSelectedItems,
}: UpgradeDrawerProps) {
  const dispatch = useAppDispatch();
  const [submitted, setSubmitted] = useState(false);
  const upgradeRows: ReadonlyArray<UpgradeRow> = useMemo(() => {
    return selectedItems
      .filter((device) => device.needs_firmware_upgrade)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((device) => {
        const eligibility = getUpgradeEligibility(device);
        return { device, ...eligibility };
      });
  }, [selectedItems]);

  const validDeviceIds = useMemo(() => {
    return upgradeRows.filter((r) => r.isValid).map((r) => r.device.id);
  }, [upgradeRows]);

  const handleUpgradeFirmware = useCallback(async () => {
    if (validDeviceIds.length === 0) {
      return;
    }
    setSubmitted(true);
    try {
      await createPromiseApiAction(
        dispatch,
        UPGRADE_DEVICES_FIRMWARE,
        validDeviceIds,
      );
      showSuccess(
        `Request processed successfully. Your ${validDeviceIds.length} devices will be upgraded shortly.`,
      );
    } catch (e) {
      showError(
        "An error occurred while attempting to upgrade the selected devices.",
      );
    } finally {
      setSelectedItems([]);
      setSubmitted(false);
      onCancel();
    }
  }, [dispatch, onCancel, setSelectedItems, validDeviceIds]);

  const drawerHeader = (
    <Alert dense severity="warning" title="Double-check the player list">
      You will <b>not</b> be able to revert this change.
    </Alert>
  );

  return (
    <Drawer
      commitDisabled={validDeviceIds.length === 0 || submitted}
      commitLabel="Upgrade Firmware"
      commitLoading={submitted}
      onCancel={onCancel}
      onClose={onCancel}
      onCommit={handleUpgradeFirmware}
      open={open}
      padding={0}
      title="Upgrade Firmware"
    >
      <BaseDrawer drawerHeader={drawerHeader}>
        {upgradeRows.map(({ device, isValid, reason }) => (
          <ListWrapper disablePadding key={device.id}>
            <UpgradeListItem
              device={device}
              isValid={isValid}
              reason={reason}
            />
          </ListWrapper>
        ))}
      </BaseDrawer>
    </Drawer>
  );
}

export const UpgradeListItem = ({ device, isValid, reason }: UpgradeRow) => {
  const ListComponent = isValid ? ListItem : ErrorListItem;

  return (
    <ListComponent>
      <Stack alignItems="center" direction="row" spacing={2}>
        {isValid ? (
          <CheckFilledIcon color="success" />
        ) : (
          <ErrorStopSignOutlinedIcon color="error" />
        )}
        <Stack flex={1}>
          <Tooltip title={device.device_id}>
            <Typography color={Colors.gray1000} variant="body1">
              {device.name}
            </Typography>
          </Tooltip>
          <Typography variant="body2">
            Firmware update: {device.current_firmware} →{" "}
            {device.latest_firmware}
            {!isValid && reason && ` - ${reason}`}
          </Typography>
        </Stack>
      </Stack>
    </ListComponent>
  );
};
